import { CANVAS_WIDTH, CANVAS_HEIGHT, BULLET_SPEED, ENEMY_TYPES, ENEMY_HORIZONTAL_SPEED } from '../config/constants.js';

export class Enemy {
  constructor(x, y, typeId = 'basic', levelMultiplier = 1) {
    this.x = x;
    this.y = y;

    // Get enemy type configuration
    const typeKey = Object.keys(ENEMY_TYPES).find(
      key => ENEMY_TYPES[key].id === typeId
    );
    this.typeConfig = typeKey ? ENEMY_TYPES[typeKey] : ENEMY_TYPES.BASIC;

    // Use custom hitbox sizes if defined, otherwise default to 29x29
    this.width = this.typeConfig.hitboxWidth || 29;
    this.height = this.typeConfig.hitboxHeight || 29;

    this.type = this.typeConfig.id;
    this.health = Math.floor(this.typeConfig.health * levelMultiplier);
    this.maxHealth = this.health;
    this.baseSpeed = this.typeConfig.speed;
    this.vx = this.baseSpeed;
    this.vy = 0;

    // Staggered firing: each enemy has a "firing enabled" time that must pass before they can shoot
    // Only 2-3 enemies can fire in the first 2 seconds - most must wait 3-8 seconds
    // 20% of enemies can fire early (1.5-3s), 80% must wait longer (3-8s)
    const isEarlyShooter = Math.random() < 0.2; // Only 20% can be early shooters
    this.firingEnabledTime = isEarlyShooter
      ? (1500 + Math.random() * 1500)  // Early: 1.5-3 seconds
      : (3000 + Math.random() * 5000); // Late: 3-8 seconds
    this.gameStartTime = Date.now(); // Track when this enemy was created
    this.lastFireTime = 0;
    this.fireRate = this.typeConfig.fireRate;
    this.points = this.typeConfig.points;
    this.behavior = this.typeConfig.behavior;

    // Load sprite image if available
    this.spriteImage = null;
    if (this.typeConfig.sprite) {
      this.spriteImage = new Image();
      this.spriteImage.src = this.typeConfig.sprite;
    }

    // Behavior-specific properties
    this.behaviorTimer = 0;
    this.behaviorState = 0;
    this.originalX = x;
    this.originalY = y;
    this.targetX = x;
    this.targetY = y;
    this.chargeDelay = 3000 + Math.random() * 2000; // For charger
    this.orbitAngle = Math.random() * Math.PI * 2; // For orbiter
    this.orbitRadius = 40; // For orbiter
    this.phaseTimer = 0; // For phaser
    this.phaseDelay = 4000; // For phaser
    this.zigzagDirection = 1; // For zigzag
    this.swoopeState = 'idle'; // For swooper
    // For frog pattern
    this.frogSequenceIndex = 0;
    this.frogFiringTimer = 0; // Timer for sequential fan firing
    this.frogFiringDelay = 150; // ms between each bullet in the fan sequence
    this.descendSpeed = 0.5; // Slow descent while moving side-to-side
    this.frogMovingRight = this.vx > 0; // Track movement direction for fan sequence
  }

  update(deltaTime, player = null) {
    const dt = deltaTime || 16;
    this.behaviorTimer += dt;
    this.deltaTime = dt; // Store for use in update methods
    this.phaseTimer += dt; // Increment phaseTimer for phaser behavior

    // Use behavior-specific update methods
    switch (this.behavior) {
      case 'frog_pattern':
        this.updateFrogPattern();
        break;
      case 'stationary':
        this.updateStationary();
        break;
      case 'bomber':
        this.updateBomber();
        break;
      case 'zigzag':
        this.updateZigzag();
        break;
      case 'tank':
        this.updateTank();
        break;
      case 'swooper':
        this.updateSwooper(player);
        break;
      case 'phaser':
        this.updatePhaser();
        break;
      case 'splitter':
      case 'horizontal_bounce':
      default:
        this.updateHorizontalBounce();
        break;
    }
  }

  updateHorizontalBounce() {
    // Space Invaders style: consistent horizontal speed, deltaTime in ms -> convert to seconds
    const direction = Math.sign(this.vx);
    this.x += direction * ENEMY_HORIZONTAL_SPEED * (this.deltaTime / 1000);

    // Bounce off walls
    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
      this.y += 10; // Move down when bouncing
    }
  }

  updateStationary() {
    // Turrets don't move
  }

  updateBomber() {
    // Slow horizontal movement
    const moveScale = 0.0008;
    this.x += this.vx * moveScale * this.deltaTime;
    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
    }
  }

  updateCharger(player) {
    if (!player) {
      this.updateHorizontalBounce();
      return;
    }

    const moveScale = 0.0008;

    // Charge at player periodically
    if (this.behaviorTimer > this.chargeDelay) {
      this.behaviorState = 1; // Charging
      this.behaviorTimer = 0;
      this.chargeDelay = 3000 + Math.random() * 2000;

      // Calculate direction to player
      const dx = player.x - this.x;
      const dy = player.y - this.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 0) {
        this.vx = (dx / distance) * this.baseSpeed * 4;
        this.vy = (dy / distance) * this.baseSpeed * 4;
      }

      setTimeout(() => {
        this.behaviorState = 0;
        this.vx = this.baseSpeed;
        this.vy = 0;
      }, 1000);
    }

    if (this.behaviorState === 1) {
      this.x += this.vx * moveScale * this.deltaTime;
      this.y += this.vy * moveScale * this.deltaTime;
    } else {
      this.updateHorizontalBounce();
    }
  }

  updateSniper() {
    // Very slow horizontal movement
    const moveScale = 0.0008;
    this.x += this.vx * moveScale * this.deltaTime;
    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
    }
  }

  updateZigzag() {
    // Erratic zigzag pattern
    const moveScale = 0.0008;
    this.x += this.vx * moveScale * this.deltaTime;
    this.y += Math.sin(this.behaviorTimer / 200) * 2 * this.zigzagDirection;

    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
      this.zigzagDirection *= -1; // Change zigzag direction
    }
  }

  updateFrogPattern() {
    // Side-to-side movement while slowly descending
    const moveScale = 0.0008;
    this.x += this.vx * moveScale * this.deltaTime;

    // Slow descent
    this.y += this.descendSpeed;

    // Bounce off walls and update movement direction tracking
    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
      this.frogMovingRight = this.vx > 0; // Update direction for firing pattern
    }
  }

  updateTank() {
    // Slow but steady horizontal movement
    const moveScale = 0.0008;
    this.x += this.vx * moveScale * this.deltaTime;
    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
      this.y += 5; // Move down slower than basic
    }
  }

  updateSwooper(player) {
    if (!player) {
      this.updateHorizontalBounce();
      return;
    }

    const moveScale = 0.0008;

    switch (this.swoopeState) {
      case 'idle':
        // Wait at top, then swoop
        if (this.behaviorTimer > 2000) {
          this.swoopeState = 'swooping';
          this.behaviorTimer = 0;
          this.targetX = player.x;
          this.targetY = player.y;
        }
        break;
      case 'swooping':
        // Dive toward player's last position
        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > 5) {
          this.x += (dx / distance) * this.baseSpeed * 2 * moveScale * this.deltaTime;
          this.y += (dy / distance) * this.baseSpeed * 2 * moveScale * this.deltaTime;
        } else {
          this.swoopeState = 'returning';
          this.targetX = this.originalX;
          this.targetY = this.originalY;
        }
        break;
      case 'returning':
        // Return to original position
        const rdx = this.targetX - this.x;
        const rdy = this.targetY - this.y;
        const rdistance = Math.sqrt(rdx * rdx + rdy * rdy);
        if (rdistance > 5) {
          this.x += (rdx / rdistance) * this.baseSpeed * moveScale * this.deltaTime;
          this.y += (rdy / rdistance) * this.baseSpeed * moveScale * this.deltaTime;
        } else {
          this.swoopeState = 'idle';
          this.behaviorTimer = 0;
        }
        break;
    }
  }

  updateOrbiter() {
    // Circle around original position
    const angleSpeed = 0.02 * (this.deltaTime / 16.67); // Normalized to 60fps
    this.orbitAngle += angleSpeed;
    this.x = this.originalX + Math.cos(this.orbitAngle) * this.orbitRadius;
    this.y = this.originalY + Math.sin(this.orbitAngle) * this.orbitRadius;
  }

  updatePhaser() {
    // phaseTimer is already incremented in update() method
    // Teleport periodically
    if (this.phaseTimer > this.phaseDelay) {
      this.phaseTimer = 0;
      this.phaseDelay = 3000 + Math.random() * 2000;

      // Teleport to new random position
      this.x = 50 + Math.random() * (CANVAS_WIDTH - 100);
      this.y = 60 + Math.random() * 150;
    }
  }

  canFire(currentTime) {
    // Check if firing is enabled yet (staggered start)
    const timeSinceSpawn = Date.now() - this.gameStartTime;
    if (timeSinceSpawn < this.firingEnabledTime) {
      return false; // Can't fire until enabled time passes
    }

    const timeSinceFire = currentTime - this.lastFireTime;

    // Different fire probabilities for different types
    if (this.type === 'turret') {
      return timeSinceFire > this.fireRate && Math.random() < 0.05;
    } else if (this.type === 'sniper') {
      return timeSinceFire > this.fireRate && Math.random() < 0.008;
    } else if (this.type === 'orbiter') {
      return timeSinceFire > this.fireRate && Math.random() < 0.02;
    }

    return timeSinceFire > this.fireRate && Math.random() < 0.01;
  }

  fire(currentTime, player = null) {
    this.lastFireTime = currentTime;

    // Different firing patterns based on type
    switch (this.type) {
      case 'frog':
        // Sequential fan spray based on movement direction
        // Moving right: fire 1, 2, 3, 4 (left to right angles)
        // Moving left: fire 4, 3, 2, 1 (right to left angles)

        // Define 4 fan directions (left, mid-left, mid-right, right)
        const fanAngles = [-Math.PI / 8, -Math.PI / 16, Math.PI / 16, Math.PI / 8];

        // Determine firing order based on movement direction
        const angleIndex = this.frogMovingRight
          ? this.frogSequenceIndex  // 0, 1, 2, 3 (left to right)
          : (3 - this.frogSequenceIndex); // 3, 2, 1, 0 (right to left)

        // Increment sequence for next shot (cycles 0→1→2→3→0)
        this.frogSequenceIndex = (this.frogSequenceIndex + 1) % 4;

        const bullets = [];
        const frogAngle = fanAngles[angleIndex];

        // Fire single bullet at the current angle in the sequence
        bullets.push({
          x: this.x,
          y: this.y + this.height / 2,
          vx: Math.sin(frogAngle) * 2,
          vy: Math.cos(frogAngle) * 2 + 1.5,
          damage: 1,
          friendly: false
        });

        return bullets;

      case 'bomber':
        // Drop 3 bombs in a spread
        return [
          { x: this.x - 15, y: this.y + this.height / 2, vx: -0.5, vy: 2, damage: 1, friendly: false },
          { x: this.x, y: this.y + this.height / 2, vx: 0, vy: 2.5, damage: 1, friendly: false },
          { x: this.x + 15, y: this.y + this.height / 2, vx: 0.5, vy: 2, damage: 1, friendly: false }
        ];

      case 'sniper':
        // Aimed shot at player
        if (player) {
          const dx = player.x - this.x;
          const dy = player.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const speed = 4;
          return {
            x: this.x,
            y: this.y + this.height / 2,
            vx: (dx / distance) * speed,
            vy: (dy / distance) * speed,
            damage: 1,
            friendly: false
          };
        }
        // Fallthrough to default if no player

      case 'tank':
        // Heavy, slow bullets
        return {
          x: this.x,
          y: this.y + this.height / 2,
          vx: 0,
          vy: 2,
          damage: 2, // Tank does more damage
          friendly: false
        };

      case 'orbiter':
        // Fires in direction it's moving
        const angle = this.orbitAngle + Math.PI / 2;
        return {
          x: this.x,
          y: this.y + this.height / 2,
          vx: Math.cos(angle) * 1.5,
          vy: Math.sin(angle) * 1.5 + 2,
          damage: 1,
          friendly: false
        };

      default:
        // Standard shot
        return {
          x: this.x,
          y: this.y + this.height / 2,
          vx: 0,
          vy: BULLET_SPEED / 2,
          damage: 1,
          friendly: false
        };
    }
  }

  takeDamage(damage) {
    this.health -= damage;
    return this.health <= 0;
  }

  // Called when killed - returns array of new enemies if splitting
  onDeath() {
    if (this.type === 'splitter') {
      // Split into 2 smaller enemies
      return [
        { type: 'basic', x: this.x - 20, y: this.y },
        { type: 'basic', x: this.x + 20, y: this.y }
      ];
    }
    return [];
  }

  render(ctx) {
    ctx.save();

    // Type-specific visual effects
    if (this.type === 'phaser') {
      ctx.globalAlpha = 0.7 + Math.sin(Date.now() / 100) * 0.3; // Flickering
    }

    if (this.type === 'tank') {
      ctx.shadowColor = this.typeConfig.color;
      ctx.shadowBlur = 10;
    }

    // Render sprite if available, otherwise fallback to shapes
    if (this.spriteImage && this.spriteImage.complete && this.spriteImage.naturalWidth > 0) {
      ctx.drawImage(
        this.spriteImage,
        this.x - this.width / 2,
        this.y - this.height / 2,
        this.width,
        this.height
      );
    } else {
      // Fallback to colored shapes if sprite not loaded
      ctx.fillStyle = this.typeConfig.color;

      // Different shapes for different types
      if (this.type === 'orbiter') {
        // Draw as circle
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.width / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (this.type === 'tank') {
        // Draw as larger rectangle
        ctx.fillRect(
          this.x - this.width / 1.5,
          this.y - this.height / 1.5,
          this.width * 1.3,
          this.height * 1.3
        );
      } else {
        // Standard rectangle
        ctx.fillRect(
          this.x - this.width / 2,
          this.y - this.height / 2,
          this.width,
          this.height
        );
      }
    }

    // Health bar for enemies with > 1 health
    if (this.maxHealth > 1) {
      const barWidth = this.width;
      const barHeight = 3;
      const healthPercent = this.health / this.maxHealth;

      ctx.shadowBlur = 0;
      ctx.fillStyle = '#202b56';
      ctx.fillRect(
        this.x - barWidth / 2,
        this.y - this.height / 2 - 6,
        barWidth,
        barHeight
      );

      ctx.fillStyle = '#ff4b6a';
      ctx.fillRect(
        this.x - barWidth / 2,
        this.y - this.height / 2 - 6,
        barWidth * healthPercent,
        barHeight
      );
    }

    ctx.restore();
  }
}

export class Boss extends Enemy {
  constructor(x, y, level, bossCharacter = null, isMini = false, isShieldBoss = false) {
    super(x, y, 'boss', 1);
    this.type = 'boss';
    this.isMini = isMini; // Mini Beeple from split
    this.isDog = false; // Track if this is a spawned dog (not the main boss)
    this.fixedProjectile = null; // Fixed projectile for dogs (assigned on creation)
    this.isShieldBoss = isShieldBoss; // Shield boss (defensive, minimal shooting)
    this.health = isMini ? 40 : (50 + level * 20); // Mini bosses have less health
    this.maxHealth = this.health;

    // Shield boss properties
    this.shieldAngle = 0; // For rotating shield visual
    this.shieldActive = isShieldBoss; // Shield bosses always have shield active
    this.width = isMini ? 50 : 67; // Increased by 5%: mini 48→50, full 64→67
    this.height = isMini ? 50 : 67; // Increased by 5%: mini 48→50, full 64→67
    this.pattern = 0;
    this.points = isMini ? 500 : 1000;
    this.bossCharacter = bossCharacter; // Character from opposite faction
    this.level = level; // Store level for pattern variety
    this.movementTimer = 0;
    this.movementPhase = 0;
    this.isBeeple = bossCharacter && bossCharacter.id === 'beeple'; // Check if this is Beeple

    // Beeple dog spawning (only for main Beeple, not mini or dogs)
    this.lastDogSpawnTime = 0;
    this.dogSpawnInterval = 5000; // Spawn a dog every 5 seconds
    this.dogSpawnCount = 0; // Track number of dogs spawned for projectile assignment

    // Use character's fire rate if available (bosses fire more frequently)
    if (this.bossCharacter && this.bossCharacter.fireRate) {
      this.fireRate = (1000 / this.bossCharacter.fireRate) * 0.7; // Boss fires faster than player
    } else {
      this.fireRate = 800; // Faster fire rate (was 500, but with better bullet speed)
    }

    // Shield bosses fire much less frequently (5x slower for easier dual boss fight)
    if (this.isShieldBoss) {
      this.fireRate *= 5;
    }

    // Level 5 dual bosses fire slower overall (both shield and aggressive)
    if (this.level === 5 && !this.isShieldBoss) {
      this.fireRate *= 1.8; // Aggressive boss fires 80% slower on level 5
    }

    // Load boss sprite if character provided
    if (this.bossCharacter && this.bossCharacter.sprite) {
      this.spriteImage = new Image();
      this.spriteImage.src = this.bossCharacter.sprite;
    }

    // Load character's projectile sprite
    this.projectileImage = null;
    if (this.bossCharacter && this.bossCharacter.projectile) {
      this.projectileImage = new Image();
      this.projectileImage.src = this.bossCharacter.projectile;
    }

    // Bosses get additional staggering for dramatic entrance
    // Override inherited lastFireTime with boss-specific delay
    const bossInitialDelay = 2000 + Math.random() * 1500; // 2000-3500ms for better player reaction time
    this.lastFireTime = -Math.random() * this.fireRate * 1.5 - bossInitialDelay;
  }

  // Get a random projectile from allProjectiles array (for Beeple boss)
  getRandomProjectile() {
    if (this.bossCharacter && this.bossCharacter.allProjectiles && this.bossCharacter.allProjectiles.length > 0) {
      const randomIndex = Math.floor(Math.random() * this.bossCharacter.allProjectiles.length);
      return this.bossCharacter.allProjectiles[randomIndex];
    }
    // Fallback to default projectile
    return this.bossCharacter ? this.bossCharacter.projectile : null;
  }

  update(deltaTime, player, separationForce = null) {
    this.deltaTime = deltaTime || 16;
    this.movementTimer += deltaTime;

    // Update shield rotation
    if (this.isShieldBoss) {
      this.shieldAngle += 0.05 * (deltaTime / 16.67);
    }

    // Shield bosses use defensive movement patterns
    if (this.isShieldBoss) {
      this.updateShieldBossMovement(player);
    } else {
      // More varied movement patterns - randomize between patterns for unpredictability
      // Use time-based cycling to alternate between different behaviors
      const cycleTime = Math.floor(this.movementTimer / 6000); // Change pattern every 6 seconds
      const patternIndex = (this.level + cycleTime) % 6; // 6 different patterns

      switch (patternIndex) {
        case 0: // Simple horizontal movement
          this.updateHorizontalBounce();
          break;
        case 1: // Horizontal with occasional rushes
          this.updateHorizontalWithRush(player);
          break;
        case 2: // Swooping from top to bottom
          this.updateSwoopPattern(player);
          break;
        case 3: // Side sweeps
          this.updateSideSweep();
          break;
        case 4: // Swoop off-screen pattern (replaced figure-eight)
          this.updateSwoopOffScreen();
          break;
        case 5: // Bubble / vertical loops
          this.updateBubbleLoops();
          break;
        default:
          this.updateHorizontalBounce();
      }
    }

    // Apply separation force to prevent overlapping with other bosses
    if (separationForce) {
      const separationStrength = 7.0; // Extremely strong separation - bosses stay far apart
      this.x += separationForce.x * separationStrength;
      this.y += separationForce.y * separationStrength;

      // Keep in bounds after separation
      this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
      this.y = Math.max(60, Math.min(CANVAS_HEIGHT / 3, this.y));
    }
  }

  updateHorizontalWithRush(player) {
    // Smooth horizontal movement with occasional controlled rushes
    if (this.movementTimer > 4000 && this.movementPhase === 0 && player) {
      this.movementPhase = 1; // Start rush
      this.rushTargetY = Math.max(60, Math.min(player.y, CANVAS_HEIGHT / 3));
      this.rushStartY = this.y;
      this.movementTimer = 0;
    }

    if (this.movementPhase === 1) {
      // Smooth rush toward player's Y position
      const progress = Math.min(this.movementTimer / 1500, 1);
      const easing = 1 - Math.pow(1 - progress, 3); // Ease out cubic
      this.y = this.rushStartY + (this.rushTargetY - this.rushStartY) * easing;

      // Continue smooth horizontal movement
      const direction = Math.sign(this.vx);
      this.x += direction * ENEMY_HORIZONTAL_SPEED * 1.2 * (this.deltaTime / 1000);

      if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
        this.vx *= -1;
      }

      if (this.movementTimer > 1500) {
        this.movementPhase = 0; // Return to normal
        this.movementTimer = 0;
      }
    } else {
      this.updateHorizontalBounce();
    }
  }

  updateSwoopPattern(player) {
    // Swooping pattern: Boss swoops down from top toward player at bottom
    const swoopCycle = this.movementTimer % 5000; // 5 second cycle

    if (swoopCycle < 2500) {
      // Phase 1: Stay at top and move horizontally
      const targetY = 70;
      this.y += (targetY - this.y) * 0.05; // Smooth return to top

      // Horizontal movement
      const direction = Math.sign(this.vx);
      this.x += direction * ENEMY_HORIZONTAL_SPEED * 1.2 * (this.deltaTime / 1000);

      if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
        this.vx *= -1;
      }
    } else {
      // Phase 2: Swoop down toward player
      if (player) {
        // Calculate swoop target (toward player, but not too far down)
        const targetX = player.x;
        const targetY = Math.min(player.y - 100, CANVAS_HEIGHT / 2); // Stop before reaching player

        // Smooth swooping movement
        const dx = (targetX - this.x) * 0.025;
        const dy = (targetY - this.y) * 0.04;

        this.x += dx;
        this.y += dy;

        // Keep in bounds
        this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
        this.y = Math.max(60, Math.min(CANVAS_HEIGHT / 2, this.y));
      }
    }
  }

  updateSideSweep() {
    // Smooth side-to-side sweep with gentle vertical wave
    const direction = Math.sign(this.vx);
    this.x += direction * ENEMY_HORIZONTAL_SPEED * 1.3 * (this.deltaTime / 1000);

    // Gentle vertical wave (not erratic)
    this.y = 70 + Math.sin(this.movementTimer / 800) * 15;

    if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
      this.vx *= -1;
    }
  }

  updateSwoopOffScreen() {
    // Swoop pattern: Boss swoops down, exits screen completely, then re-enters from top
    const swoopCycle = this.movementTimer % 8000; // 8 second cycle

    if (swoopCycle < 3000) {
      // Phase 1: Move at top horizontally
      const targetY = 70;
      this.y += (targetY - this.y) * 0.05;

      const direction = Math.sign(this.vx);
      this.x += direction * ENEMY_HORIZONTAL_SPEED * 1.2 * (this.deltaTime / 1000);

      if (this.x <= this.width / 2 || this.x >= CANVAS_WIDTH - this.width / 2) {
        this.vx *= -1;
      }
    } else if (swoopCycle < 5000) {
      // Phase 2: Swoop down - continue all the way off bottom of screen
      this.y += 3.5; // Fast downward movement

      // Continue horizontal movement during swoop
      const direction = Math.sign(this.vx);
      this.x += direction * ENEMY_HORIZONTAL_SPEED * 0.8 * (this.deltaTime / 1000);
    } else {
      // Phase 3: Continue moving down until completely off-screen, then re-enter from top
      if (this.y > CANVAS_HEIGHT + this.height) {
        // Completely off-screen - reset to top
        this.y = -this.height;
        this.x = Math.random() * (CANVAS_WIDTH - 100) + 50;
      } else if (this.y < 70) {
        // Rising back to normal position after teleporting to top
        this.y += 2;
      } else {
        // Still swooping down until completely off-screen (prevents blank out)
        this.y += 3.5;
        const direction = Math.sign(this.vx);
        this.x += direction * ENEMY_HORIZONTAL_SPEED * 0.8 * (this.deltaTime / 1000);
      }
    }
  }

  updateBubbleLoops() {
    // Vertical looping/bubble pattern
    const loopCycle = this.movementTimer % 6000; // 6 second cycle
    const centerX = CANVAS_WIDTH / 2;

    // Oscillate around center with vertical loops
    const verticalSpeed = Math.sin(loopCycle / 1000) * 60; // Varying vertical movement
    const horizontalOffset = Math.cos(loopCycle / 1500) * 80; // Gentle horizontal sway

    this.x = centerX + horizontalOffset;
    this.y = 100 + verticalSpeed;

    // Keep in bounds
    this.y = Math.max(60, Math.min(CANVAS_HEIGHT / 3, this.y));
    this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
  }

  updateAggressiveCharge(player) {
    // Predictable charge pattern with clear telegraph
    if (this.movementTimer > 2500) {
      this.movementPhase = (this.movementPhase + 1) % 2;
      this.movementTimer = 0;

      if (this.movementPhase === 1 && player) {
        // Store target position for smooth movement
        this.chargeTargetX = player.x;
        this.chargeTargetY = Math.min(player.y, CANVAS_HEIGHT / 3);
      }
    }

    if (this.movementPhase === 1 && this.chargeTargetX !== undefined) {
      // Smooth charge toward stored target
      const dx = (this.chargeTargetX - this.x) * 0.04;
      const dy = (this.chargeTargetY - this.y) * 0.03;
      this.x += dx;
      this.y += dy;

      // Keep in bounds
      this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
      this.y = Math.max(this.height / 2, Math.min(CANVAS_HEIGHT / 3, this.y));
    } else {
      this.updateHorizontalBounce();
    }
  }

  updateShieldBossMovement(player) {
    // Shield boss: defensive, evasive movement - dodges away from player
    // Stays at top of screen and moves side-to-side while avoiding player's X position

    if (player) {
      // Calculate distance from player
      const dx = this.x - player.x;
      const dy = this.y - player.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If too close to player, dodge away
      if (distance < 150) {
        // Move away from player horizontally
        const dodgeSpeed = 2.5;
        if (dx > 0) {
          // Player is to the left, dodge right
          this.x += dodgeSpeed;
        } else {
          // Player is to the right, dodge left
          this.x -= dodgeSpeed;
        }
      } else {
        // Gentle horizontal movement when safe
        const driftSpeed = Math.sin(this.movementTimer / 800) * 1.5;
        this.x += driftSpeed;
      }

      // Stay near top of screen (defensive positioning)
      const targetY = 70 + Math.sin(this.movementTimer / 1000) * 10; // Slight vertical drift
      this.y += (targetY - this.y) * 0.03; // Smooth movement toward target Y
    } else {
      // No player - just drift gently
      this.x += Math.sin(this.movementTimer / 800) * 1.5;
      this.y = 70 + Math.sin(this.movementTimer / 1000) * 10;
    }

    // Keep in bounds
    this.x = Math.max(this.width / 2, Math.min(CANVAS_WIDTH - this.width / 2, this.x));
    this.y = Math.max(60, Math.min(CANVAS_HEIGHT / 3, this.y));
  }

  fire(currentTime, player) {
    this.lastFireTime = currentTime;

    // Boss bullets match regular enemy speed exactly (same as BULLET_SPEED / 2)
    const bulletSpeed = BULLET_SPEED / 2; // 140, same as regular enemies

    // Get character ID for tracking hit effects
    const sourceCharacterId = this.bossCharacter ? (this.bossCharacter.id || this.bossCharacter.name?.toLowerCase()) : null;

    let bullets;

    // Beeple dogs fire single projectiles only (much easier than main Beeple)
    // Each dog uses its assigned fixedProjectile for consistent appearance
    if (this.isDog) {
      bullets = [{
        x: this.x,
        y: this.y + this.height / 2,
        vx: 0,
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.fixedProjectile || this.getRandomProjectile(),
        color: '#b966ff' // Purple like Beeple
      }];
    }
    // Beeple uses special firework spiral pattern (full-size Beeple only)
    else if (this.isBeeple && !this.isMini) {
      bullets = this.fireFireworkSpiral(bulletSpeed);
    } else {
      // Specific boss attack patterns with clear identity
      const patternIndex = this.level % 5;

      switch (patternIndex) {
        case 0: // Level 1: Burst/Spread (EASIEST - from Boss 2)
          bullets = this.fireBurstSpread(bulletSpeed, player);
          break;
        case 1: // Level 2: Spiral pattern (medium difficulty)
          bullets = this.fireSpiralPattern(bulletSpeed);
          break;
        case 2: // Level 3: Triple spread (harder)
          bullets = this.fireTripleSpread(bulletSpeed);
          break;
        case 3: // Level 4: Spinning projectiles (hardest pattern)
          bullets = this.fireSpinningProjectiles(bulletSpeed);
          break;
        case 4: // Level 5: Mixed aggressive patterns
          this.pattern = (this.pattern + 1) % 3;
          if (this.pattern === 0) bullets = this.fireBurstSpread(bulletSpeed, player);
          else if (this.pattern === 1) bullets = this.fireSpiralPattern(bulletSpeed);
          else bullets = this.fireSpinningProjectiles(bulletSpeed);
          break;
        default:
          bullets = this.fireTripleSpread(bulletSpeed);
      }
    }

    // Add sourceCharacterId to all bullets for character-specific hit effects
    if (Array.isArray(bullets)) {
      bullets.forEach(b => b.sourceCharacterId = sourceCharacterId);
    } else if (bullets) {
      bullets.sourceCharacterId = sourceCharacterId;
    }

    return bullets;
  }

  fireTripleSpread(bulletSpeed) {
    // Wider spacing for dodgeability
    // Red bullets - straight down shots
    return [
      {
        x: this.x - 20,
        y: this.y + this.height / 2,
        vx: -2.2, // Increased from -1.5
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: '#ff4b6a' // Red for straight shots
      },
      {
        x: this.x,
        y: this.y + this.height / 2,
        vx: 0,
        vy: bulletSpeed + 1,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: '#ff4b6a' // Red for straight shots
      },
      {
        x: this.x + 20,
        y: this.y + this.height / 2,
        vx: 2.2, // Increased from 1.5
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: '#ff4b6a' // Red for straight shots
      }
    ];
  }

  fireFiveWayBurst(bulletSpeed) {
    return [
      {
        x: this.x - 30,
        y: this.y + this.height / 2,
        vx: -2,
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: this.bossCharacter ? this.bossCharacter.particleColor : '#ff4b6a'
      },
      {
        x: this.x - 15,
        y: this.y + this.height / 2,
        vx: -1,
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: this.bossCharacter ? this.bossCharacter.particleColor : '#ff4b6a'
      },
      {
        x: this.x,
        y: this.y + this.height / 2,
        vx: 0,
        vy: bulletSpeed + 1,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: this.bossCharacter ? this.bossCharacter.particleColor : '#ff4b6a'
      },
      {
        x: this.x + 15,
        y: this.y + this.height / 2,
        vx: 1,
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: this.bossCharacter ? this.bossCharacter.particleColor : '#ff4b6a'
      },
      {
        x: this.x + 30,
        y: this.y + this.height / 2,
        vx: 2,
        vy: bulletSpeed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: this.bossCharacter ? this.bossCharacter.particleColor : '#ff4b6a'
      }
    ];
  }

  fireAimedShots(bulletSpeed, player) {
    if (!player) return this.fireTripleSpread(bulletSpeed);

    // Fire bullets aimed at player's position
    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const vx = (dx / dist) * bulletSpeed * 0.7;
    const vy = (dy / dist) * bulletSpeed * 0.7;

    return [
      {
        x: this.x,
        y: this.y + this.height / 2,
        vx: vx,
        vy: vy,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: this.bossCharacter ? this.bossCharacter.particleColor : '#ff4b6a'
      }
    ];
  }

  fireSpiralPattern(bulletSpeed) {
    // Continuously rotating spiral pattern (3-4 arms)
    // Purple bullets - circular/spiral pattern
    const angle = (this.movementTimer / 80) % (Math.PI * 2);
    const count = 4;
    const bullets = [];

    for (let i = 0; i < count; i++) {
      const a = angle + (i * Math.PI * 2 / count);
      const speed = bulletSpeed * 0.8;
      bullets.push({
        x: this.x,
        y: this.y + this.height / 2,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed + bulletSpeed * 0.3,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: '#b877ff' // Purple for spiral/circular shots
      });
    }

    return bullets;
  }

  fireSpinningProjectiles(bulletSpeed) {
    // Spinning/orbiting projectiles that orbit briefly then release outward
    // Cyan bullets - orbiting/spinning pattern
    const angle = (this.movementTimer / 120) % (Math.PI * 2);
    const count = 6;
    const bullets = [];
    const orbitRadius = 40;

    for (let i = 0; i < count; i++) {
      const a = angle + (i * Math.PI * 2 / count);
      // Bullets start at orbit position and move outward
      const startX = this.x + Math.cos(a) * orbitRadius;
      const startY = this.y + Math.sin(a) * orbitRadius;
      const speed = bulletSpeed * 0.6;

      bullets.push({
        x: startX,
        y: startY,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed + bulletSpeed * 0.4,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: '#44ddff' // Cyan for orbiting/spinning shots
      });
    }

    return bullets;
  }

  fireBurstSpread(bulletSpeed, player) {
    // Fan/cone pattern toward player (burst/spread) - dodgeable with clear lanes
    // Orange bullets - fan/spread pattern
    if (!player) return this.fireTripleSpread(bulletSpeed);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const baseAngle = Math.atan2(dy, dx);
    const spread = Math.PI / 3; // 60-degree spread (increased from 45°)
    const count = 5; // Reduced from 7 for wider gaps
    const bullets = [];

    for (let i = 0; i < count; i++) {
      const angle = baseAngle + (i - (count - 1) / 2) * (spread / (count - 1));
      const speed = bulletSpeed * 0.9;

      bullets.push({
        x: this.x,
        y: this.y + this.height / 2,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(),
        color: '#ff9944' // Orange for fan/spread shots
      });
    }

    return bullets;
  }

  fireFireworkSpiral(bulletSpeed) {
    // Beeple's signature "firework" pattern: rotating radial burst with safe lanes
    // Purple bullets - creates a rotating spiral/radial pattern like a firework
    const rotationAngle = (this.movementTimer / 100) % (Math.PI * 2); // Slower rotation for readability
    const count = 10; // 10 projectiles create clear gaps (36° between each)
    const bullets = [];

    for (let i = 0; i < count; i++) {
      const angle = rotationAngle + (i * Math.PI * 2 / count);
      const speed = bulletSpeed * 0.85; // Slightly slower for dodgeability

      bullets.push({
        x: this.x,
        y: this.y + this.height / 2,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed + bulletSpeed * 0.25, // Slight downward bias
        damage: 1,
        friendly: false,
        isBossProjectile: true,
        sprite: this.getRandomProjectile(), // Mix of different boss projectiles
        color: '#b966ff' // Purple with glow (matches enemy projectiles)
      });
    }

    return bullets;
  }

  // Override onDeath for Beeple (no longer splits)
  onDeath() {
    // Beeple no longer splits on death
    // Dogs are spawned periodically during the fight instead
    return [];
  }

  // Check if it's time to spawn a dog (called by GameEngine)
  shouldSpawnDog(currentTime) {
    // Only main Beeple spawns dogs (not mini bosses or dogs themselves)
    if (!this.isBeeple || this.isMini || this.isDog) {
      return false;
    }

    // Check if enough time has passed since last spawn
    if (currentTime - this.lastDogSpawnTime >= this.dogSpawnInterval) {
      this.lastDogSpawnTime = currentTime;
      return true;
    }

    return false;
  }

  // Create dog spawn data
  createDogSpawn() {
    // Assign each dog a unique projectile by cycling through allProjectiles
    const allProjectiles = this.bossCharacter?.allProjectiles || [];
    const projectileIndex = this.dogSpawnCount % Math.max(allProjectiles.length, 1);
    const dogProjectile = allProjectiles.length > 0 ? allProjectiles[projectileIndex] : this.bossCharacter?.projectile;

    this.dogSpawnCount++; // Increment for next dog

    const beepleDogCharacter = {
      ...this.bossCharacter,
      id: 'beeple_dog',
      name: 'Beeple Dog',
      displayName: 'Beeple Dog',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/693439b3c7048c189e38ade4_beeple_dog.png'
    };

    // Spawn dog at a random position near Beeple
    const offsetX = (Math.random() - 0.5) * 100; // Random offset within 100px
    const spawnX = Math.max(30, Math.min(330, this.x + offsetX)); // Keep within bounds

    return {
      type: 'beeple_dog',
      x: spawnX,
      y: this.y + 40, // Spawn below Beeple
      bossCharacter: beepleDogCharacter,
      level: this.level,
      isMini: true,
      isDog: true,
      fixedProjectile: dogProjectile // Each dog gets a unique projectile
    };
  }

  render(ctx) {
    ctx.save();

    // Boss glow
    ctx.shadowColor = '#ff4b6a';
    ctx.shadowBlur = 15;

    // Render boss sprite or fallback to colored rectangle
    if (this.spriteImage && this.spriteImage.complete && this.spriteImage.naturalWidth > 0) {
      ctx.drawImage(
        this.spriteImage,
        this.x - this.width / 2,
        this.y - this.height / 2,
        this.width,
        this.height
      );
    } else {
      ctx.fillStyle = '#ff4b6a';
      ctx.fillRect(
        this.x - this.width / 2,
        this.y - this.height / 2,
        this.width,
        this.height
      );
    }

    // Boss health bar
    const barWidth = 80;
    const barHeight = 5;
    const healthPercent = this.health / this.maxHealth;

    ctx.shadowBlur = 0;
    ctx.fillStyle = '#202b56';
    ctx.fillRect(
      this.x - barWidth / 2,
      this.y - this.height / 2 - 10,
      barWidth,
      barHeight
    );

    ctx.fillStyle = '#ffe38f';
    ctx.fillRect(
      this.x - barWidth / 2,
      this.y - this.height / 2 - 10,
      barWidth * healthPercent,
      barHeight
    );

    // Render rotating shield for shield bosses
    if (this.isShieldBoss) {
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#44ddff';

      // Draw 4 shield segments rotating around the boss
      const shieldRadius = this.width / 2 + 8;
      const segmentCount = 4;

      for (let i = 0; i < segmentCount; i++) {
        const angle = this.shieldAngle + (i * Math.PI * 2 / segmentCount);
        const x1 = this.x + Math.cos(angle) * shieldRadius;
        const y1 = this.y + Math.sin(angle) * shieldRadius;

        // Draw shield segment as small arc
        ctx.strokeStyle = '#44ddff';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(this.x, this.y, shieldRadius, angle - 0.3, angle + 0.3);
        ctx.stroke();
      }
    }

    ctx.restore();
  }
}
